import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { ToastController } from '@ionic/angular';
import { MoodAudioService, MoodAudioState } from '../../../services/mood-audio.service';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { SoundPickerComponent } from '../../../components/sound-picker/sound-picker.component';
import { PendingToggleGate } from 'src/app/shared/helpers/pending-toggle-gate';
import { AnalyticsService } from 'src/app/services/analytics.service';
import {
  DEFAULT_DURATION, DEFAULT_VOLUME, MoodDuration, SOUND_NAME_MAX, deriveName, truncateSoundName,
} from '../../../consts/mood-audio';

/**
 * Static transport bar above the Scenes carousel: one on/off control, the current
 * sound's name, and a menu that opens the shared Sound | Duration wheel.
 *
 * The transport is two states, nothing more:
 *
 *   off  ▶   tap → start the current selection
 *   on   ‖   tap → END the session
 *
 * "End", not "pause". Pause would leave the unit's session flag on, the timer
 * paused and the amplifier still on network input, so Bluetooth is never restored
 * and the amp stays claimed indefinitely — the playback-ended automation can't
 * rescue it either, because a paused LinkPlay reports `paused`, not `idle`. Off
 * therefore calls mood_audio_stop, which restores `source: bluetooth`, cancels the
 * timer and clears the flag. The ‖ glyph is kept because for a looping ambient
 * sound "paused" and "stopped" are indistinguishable to a resident; the difference
 * is only whether the hardware is released, and it should be.
 *
 * `paused` arriving from the unit therefore reads as ON, so a session started
 * elsewhere (a scene, say) can always be ended here.
 *
 * Optimistic state runs through PendingToggleGate. getMessageListener re-emits the
 * whole device-state map on any unrelated update, so without the gate a
 * not-yet-caught-up echo would overwrite the optimistic value and make the control
 * visibly bounce — the exact bug that class was written for. One boolean, one gate:
 * "is a session live".
 */
@Component({
  selector: 'mood-audio-bar',
  templateUrl: './mood-audio-bar.component.html',
  styleUrls: ['./mood-audio-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoodAudioBarComponent implements OnInit, OnDestroy {
  @ViewChild(SoundPickerComponent) picker?: SoundPickerComponent;

  state: MoodAudioState = {
    status: 'idle', sound: null, duration: DEFAULT_DURATION, volume: DEFAULT_VOLUME,
    library: [], ampPresent: false, hydrated: false,
  };

  /**
   * Held optimistic values while a command is in flight; null means "follow the
   * device".
   *
   * 15s, raised from 12s once the timeout became destructive. The unit's start
   * script now takes the network input and waits a second before play_media, so
   * time-to-playing grew by about that much — and with retractFailedStart below,
   * timing out no longer just shows a wrong toast, it tears the session down. A
   * start confirming at 12.5s used to be a cosmetic annoyance; it would have
   * become a killed session.
   *
   * Still inside the unit's own limits, which is what keeps the two from
   * fighting: the start script gives up at ~16s and the watchdog reaps at 20s.
   * If on-unit timing differs, tune this rather than the script's timeout.
   */
  private optimisticOn: boolean | null = null;
  private readonly gate = new PendingToggleGate(15000);
  private readonly destroy$ = new Subject<void>();

  /**
   * Reachability for control, from the same source the lighting and climate
   * widgets read.
   *
   * Starts `reconnecting`, like they do — but unlike them this bar does not dim
   * on it alone. See `unavailable`: dimming waits until something has actually
   * settled, so a cold start shows a normal bar rather than flashing grey.
   */
  public controlAvailability: 'ready' | 'reconnecting' | 'needs_reauth' = 'reconnecting';

  constructor(
    private mood: MoodAudioService,
    private mqtt: MqttConnectorService,
    private toastCtrl: ToastController,
    private analytics: AnalyticsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.mqtt.getControlAvailability().pipe(takeUntil(this.destroy$)).subscribe(a => {
      this.controlAvailability = a;
      this.cdr.markForCheck();
    });
    this.mood.state$.pipe(takeUntil(this.destroy$)).subscribe(s => {
      // Clear the optimistic hold only once the device confirms the requested value; a
      // mismatching echo while pending is stale and must not win. A gate that isn't
      // pending applies everything, so this is a no-op the rest of the time.
      //
      // `paused` confirms NEITHER direction, which is why this is a three-way and
      // not `s.status === 'playing'`.
      //
      // The unit raises its session flag before playback begins (so an abort
      // mid-start is recoverable) and clears it before the amplifier catches up, and
      // it publishes flag-up-but-not-playing as `paused`. So `paused` is the
      // transitional state at BOTH ends of a session and settles nothing:
      //  - Confirming a START on it settled the gate on a session that never played:
      //    orange bar, silence, and no "Couldn't start audio." toast, because the
      //    gate had been cleared and could no longer time out.
      //  - Confirming a STOP on it made the bar bounce back to orange before going
      //    grey, because `playing` below reads `paused` as ON. The hold was released
      //    while the published state still said `paused`, so the real state showed
      //    through for the moment before `idle` arrived.
      // Left unconfirmed, the gate simply holds the optimistic value until the state
      // resolves either way, or times out and reverts with a toast.
      const settled = s.status === 'playing' ? true : s.status === 'idle' ? false : null;
      if (settled !== null && this.gate.shouldApply(settled)) {
        this.optimisticOn = null;
      }
      this.state = s;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.gate.cancel();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Always rendered, so it arrives with the rest of home rather than appearing a
   * beat later once MQTT data lands. It used to hide itself whenever there was
   * nothing to control, which made it pop in on every app open.
   *
   * Knock-on worth knowing: a unit with no amplifier at all now shows a
   * permanently dimmed bar rather than nothing. That is the deliberate choice —
   * Ezra, 2026-09-11: "when it is unavailable, make the bar and its buttons grey
   * and unclickable like the climate widget, not hidden."
   */
  get unavailable(): boolean {
    // Never dim a live session: whatever else is wrong, a resident who can hear
    // audio must be able to stop it. This is the same reason the bar used to
    // refuse to hide while playing.
    if (this.playing) { return false; }
    // Nothing known yet is not the same as nothing there.
    //
    // Two independent ways to know: the unit has populated the shadow
    // (`hydrated`), or the connection is up and simply has nothing to show. Until
    // ONE of them holds there is nothing to judge on, so the bar renders normally.
    //
    // A flag set on the first emission is not enough, which is what the first
    // attempt used: the listeners map a BehaviorSubject and fire synchronously on
    // subscribe with an empty map, so that flag was true on frame one and the bar
    // greyed instantly. Ezra: "when i refreshed the host, the audio bar is greyed
    // out instantly."
    const settled = this.state.hydrated || this.controlAvailability === 'ready';
    if (!settled) { return false; }
    return this.controlAvailability !== 'ready'
      || !this.state.ampPresent
      || this.state.library.length === 0;
  }

  /**
   * Is a session live? Real state, unless a command is in flight and we are holding its
   * expected outcome. Every button and label reads this, not `state.status`, so the
   * transport can never disagree with itself.
   *
   * `paused` counts as ON. The unit can still report it — a scene, or something else
   * driving the amp — and a held session is exactly what the resident needs the control
   * for. Reading it as OFF would leave the amplifier claimed with no way to release it.
   */
  get playing(): boolean {
    return this.optimisticOn ?? (this.state.status !== 'idle');
  }

  /**
   * The level to show on the picker's slider: what the live session is playing at,
   * or the remembered default while idle. The service picks between them — see
   * MoodAudioState.volume.
   *
   * No optimistic hold, unlike the transport. The slider is bound to this and
   * commits on knob release, so the only echo that can move it under a finger is
   * one arriving mid-drag — which the picker itself suppresses while dragging.
   */
  get volume(): number {
    return this.state.volume;
  }

  get displayName(): string {
    // The name slot carries the reason when there is nothing to name. Without it
    // a dimmed bar shows a sound the resident cannot play, or nothing at all.
    if (this.unavailable) { return 'Audio Unavailable'; }
    return truncateSoundName(this.state.sound?.name ?? '', SOUND_NAME_MAX);
  }

  /**
   * The only control: on starts the current selection, off ENDS the session.
   *
   * Off deliberately calls stop rather than pause. Pause leaves the session flag on,
   * the timer paused and the amplifier on network input — Bluetooth is never restored,
   * and the playback-ended automation cannot rescue it because a paused LinkPlay
   * reports `paused`, not `idle`. So a pause-based off would hold the amp forever.
   * Stop restores `source: bluetooth`, cancels the timer and clears the flag.
   */
  toggle(): void {
    if (this.playing) {
      this.hold(false, "Couldn't stop audio.", () => this.mood.stop(), 'stop');
      return;
    }
    // A RESOLVED sound is needed only to START one — `sound` is null when the reported
    // stem is empty AND the library is empty, which is exactly the live-session case
    // the visibility override renders the bar for. Turning OFF must work regardless,
    // which is why that path is above this guard rather than below it.
    const sound = this.state.sound;
    if (!sound) { return; }
    this.hold(true, "Couldn't start audio.", () => this.mood.play(sound.id, this.state.duration),
              'play', { sound: sound.id, duration: this.state.duration });
  }

  openPicker(): void {
    this.picker?.open();
  }

  onPicked(selection: { sound: string; duration: MoodDuration }): void {
    this.track('sound_selected', {
      sound: selection.sound, duration: selection.duration,
      started: this.playing,
    });
    // A live session switches immediately. select() only writes the helper entities, so
    // using it here would move the bar's NAME to the new sound while the amplifier still
    // held the old track. While off, staging the selection is all that's wanted.
    if (this.playing) {
      const sound = this.state.library.find(s => s.id === selection.sound)
        ?? { id: selection.sound, name: deriveName(selection.sound) };
      this.hold(true, "Couldn't start audio.", () => this.mood.play(sound.id, selection.duration),
                'play', { sound: sound.id, duration: selection.duration });
      return;
    }
    this.mood.select(selection.sound, selection.duration).pipe(take(1))
      .subscribe({ error: () => this.presentToast("Couldn't change the sound.") });
  }

  /**
   * Volume is the one control here that is NOT staged behind the picker's Set
   * button: it fires on knob release so the resident hears the level they are
   * choosing. The seam is deliberate and worth knowing about — drag the slider,
   * then dismiss the sheet without tapping Set, and the volume sticks while the
   * sound and duration changes are discarded.
   *
   * Deliberately does NOT route through play()/select(). The unit applies a live
   * level to the amplifier itself, and going through start would restart the loop
   * from the top and re-run its 15-second wait for nothing.
   */
  onVolumePicked(volume: number): void {
    this.track('volume_change', { volume, started: this.playing });
    this.mood.setVolume(volume).pipe(take(1)).subscribe({
      error: () => {
        // Snap the slider back to the unit's level before toasting, so the sheet
        // never sits there showing a volume the amplifier does not have.
        this.picker?.revertVolume();
        this.cdr.markForCheck();
        this.presentToast("Couldn't change the volume.");
      },
    });
  }

  /**
   * Transport telemetry, tracked at the tap like the scenes and lighting widgets.
   * Originally the only feedback available, since the amplifier was believed to be
   * absent from the fleet; that turned out to be wrong (the AL dev unit has one and
   * this feature was validated against it), but the events are still the only view
   * of what residents actually do with the control.
   */
  private track(action: string, extra?: Record<string, any>): void {
    this.analytics.trackWidgetInteraction('mood_audio', action, {
      ...(this.state.sound && { sound: this.state.sound.id }),
      ...extra,
    });
  }

  /**
   * Take the optimistic hold, fire the command, and revert on failure.
   *
   * Success is deliberately SILENT. A publish being accepted is not the amplifier
   * starting, so a success toast would be a claim we can't back — and on a unit without
   * the HA half it was immediately contradicted by the failure toast 12s later. The
   * orange fill is the positive feedback; toasts mean something went wrong.
   */
  private hold(
    target: boolean,
    failure: string,
    command: () => Observable<void>,
    action: string,
    extra?: Record<string, any>,
  ): void {
    this.optimisticOn = target;
    this.track(action, extra);
    const token = this.gate.begin(target, !target, () => {
      // Deliberately ignoring the gate's `previous` value and falling back to
      // "follow real state": the truth is server-owned, so the reverted bar shows
      // whatever the unit last reported rather than a value the client guessed.
      this.optimisticOn = null;
      this.cdr.markForCheck();
      this.presentToast(failure);
      if (target) {
        this.retractFailedStart();
      }
    });
    command().pipe(take(1)).subscribe({ error: () => this.gate.failIfCurrent(token) });
  }

  /**
   * Tear down a session this client started but that never produced audio.
   *
   * Without this the toast and the bar contradict each other. Falling back to
   * real state is right, but the unit's real state at that moment is a raised
   * session flag with nothing playing, which it publishes as `paused` and
   * `playing` below reads as ON. So the resident saw "Couldn't start audio."
   * over a still-orange bar, and it stayed orange for the ~8 seconds until the
   * unit's watchdog reaped the session. Observed on the AL dev unit 2026-09-11.
   *
   * Retracting is also the more honest of the two: the client asked for a
   * session, has just told the resident it failed, and a session that begins
   * playing a second later would be worse than one that does not. The watchdog
   * stays as the backstop for sessions this client is not watching — one a scene
   * started, or one left running when the app was closed.
   *
   * Errors are swallowed on purpose. The resident has already been told the
   * start failed; a second toast saying the cleanup also failed describes
   * plumbing they cannot act on, and the watchdog still covers it.
   */
  private retractFailedStart(): void {
    // Retract ONLY a session the unit says is stuck — flag up, nothing playing,
    // which it publishes as `paused`. That is the state this exists for: the
    // amplifier never started, and without a stop the session sits until the
    // unit's watchdog reaps it ~8 seconds later, with the bar orange over a
    // toast saying it failed.
    //
    // `playing` is excluded because the session is audibly fine — the timeout
    // means "no confirmation in time", not "it never started", and a
    // confirmation can land while this is being decided.
    //
    // `idle` is excluded because nothing ever reached the unit, and a stop then
    // does real damage. Commands issued while disconnected are QUEUED and
    // flushed on reconnect, so the stop does not cancel the start — it lines up
    // BEHIND it. Observed: wifi off, press play, reconnect, and the audio
    // started and was killed a moment later by this. The resident asked for
    // audio and got silence, from the cleanup rather than the request.
    if (this.state.status !== 'paused') {
      return;
    }
    this.mood.stop().pipe(take(1)).subscribe({ error: () => undefined });
  }

  private presentToast(message: string): void {
    this.toastCtrl
      .create({ message, duration: 1500, position: 'bottom', cssClass: 'scene-toast' })
      .then(toast => toast.present());
  }
}
